<template>
  <el-drawer
    :visible.sync="drawerVisible"
    direction="rtl"
    size="960px"
    :with-header="false"
    class="store-goods-image-drawer"
  >
    <div class="drawer-shell">
      <div class="drawer-head">
        <span>店铺商品图片操作</span>
        <el-button type="text" icon="el-icon-close" class="drawer-close" @click="drawerVisible = false" />
      </div>

      <div class="drawer-body">
        <section class="section-block">
          <div class="section-title">基础信息</div>
          <div class="summary-grid">
            <div v-for="item in summaryCards" :key="item.label" class="summary-card">
              <div class="summary-label">{{ item.label }}</div>
              <div class="summary-value" :title="item.value">{{ item.value }}</div>
            </div>
          </div>
        </section>

        <section class="section-block">
          <div class="section-title">处理流程展示组件</div>
          <div class="process-stepper">
            <div
              v-for="(step, index) in processSteps"
              :key="step.label"
              :class="['process-step', { active: activeStep === index + 1, done: activeStep > index + 1 }]"
            >
              <span>{{ index + 1 }}</span>
              <strong>{{ step.label }}</strong>
            </div>
          </div>
          <div class="step-desc">{{ currentStepDesc }}</div>
        </section>

        <section class="section-block">
          <div class="section-title">设计图通知信息</div>
          <el-table
            :data="noticeRows"
            border
            size="mini"
            :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
            :cell-style="{ textAlign: 'center' }"
          >
            <el-table-column prop="productModel" label="产品型号" min-width="120" show-overflow-tooltip />
            <el-table-column prop="spuName" label="商品名称" min-width="160" show-overflow-tooltip />
            <el-table-column prop="noticeDisplay" label="调整类型" min-width="160">
              <template slot-scope="{ row }">
                <span class="multiline-cell-text">{{ lineBreak(row.noticeDisplay) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="picAdjustType" label="图片类型" min-width="180">
              <template slot-scope="{ row }">
                <span class="multiline-cell-text">{{ lineBreak(row.picAdjustType) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="noticeTime" label="通知时间" width="160" show-overflow-tooltip />
            <el-table-column prop="uploadCategory" label="上传类别" min-width="120" show-overflow-tooltip />
            <el-table-column prop="adjustRemark" label="调整备注" min-width="180" show-overflow-tooltip />
          </el-table>
        </section>

        <component
          ref="stepComponent"
          :is="currentStepComponent"
          :row-data="rowData"
          :visible="visible"
          :selected-product-rows="selectedProductRows"
          :precheck-data="precheckData"
          :plan-rows="planRows"
          @basic-info-change="handleBasicInfoChange"
          @selection-change="handleSelectionChange"
          @exception-confirm-change="handleExceptionConfirmChange"
          @precheck-change="handlePrecheckChange"
          @plan-rows-change="handlePlanRowsChange"
          @preview-material="handlePreviewMaterial"
        />
      </div>

      <div class="drawer-foot">
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button @click="handlePrev" :disabled="activeStep === 1">上一步</el-button>
        <el-button type="primary" :loading="nextLoading" :disabled="isNextDisabled" @click="handleNext" v-if="activeStep < 3">下一步</el-button>
        <el-button type="primary" :loading="finishLoading" @click="handleFinish" v-else>完成</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import StoreGoodsImageStepOne from './storeGoodsImageStepOne.vue'
import StoreGoodsImageStepTwo from './storeGoodsImageStepTwo.vue'
import StoreGoodsImageStepThree from './storeGoodsImageStepThree.vue'

export default {
  name: 'StoreGoodsImageDrawer',
  components: {
    StoreGoodsImageStepOne,
    StoreGoodsImageStepTwo,
    StoreGoodsImageStepThree
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    rowData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      activeStep: 1,
      summaryCards: [],
      processSteps: [
        { label: '选择处理目标', desc: '选择平台组、店铺和商品映射。' },
        { label: '预检并确认异常', desc: '查看规则快照、处理计划和异常结果。' },
        { label: '创建并执行任务', desc: '确认后进入任务创建与执行流程。' }
      ],
      selectedPlatformRows: [],
      selectedStoreRows: [],
      selectedProductRows: [],
      nextLoading: false,
      exceptionConfirmed: false,
      precheckData: {},
      planRows: [],
      finishLoading: false
    }
  },
  computed: {
    drawerVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      }
    },
    noticeRows () {
      const row = this.rowData || {}
      return [
        {
          productModel: row.productModel || '-',
          spuName: row.spuName || '-',
          noticeDisplay: row.noticeDisplay || '-',
          picAdjustType: row.picAdjustType || '-',
          noticeTime: row.noticeTime || '-',
          uploadCategory: row.uploadCategory || '-',
          adjustRemark: row.adjustRemark || '-'
        }
      ]
    },
    currentStepDesc () {
      return this.processSteps[this.activeStep - 1] ? this.processSteps[this.activeStep - 1].desc : ''
    },
    currentStepComponent () {
      const componentMap = {
        1: 'StoreGoodsImageStepOne',
        2: 'StoreGoodsImageStepTwo',
        3: 'StoreGoodsImageStepThree'
      }
      return componentMap[this.activeStep] || 'StoreGoodsImageStepOne'
    },
    isNextDisabled () {
      return this.nextLoading || (this.activeStep === 2 && !this.exceptionConfirmed)
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.initData()
      }
    }
  },
  mounted () {
    if (this.visible) {
      this.initData()
    }
  },
  methods: {
    initData () {
      this.activeStep = 1
      this.summaryCards = []
      this.selectedPlatformRows = []
      this.selectedStoreRows = []
      this.selectedProductRows = []
      this.exceptionConfirmed = false
      this.precheckData = {}
      this.planRows = []
      this.finishLoading = false
    },
    handleBasicInfoChange (cards) {
      this.summaryCards = Array.isArray(cards) ? cards : []
    },
    handleSelectionChange (payload) {
      const data = payload || {}
      this.selectedPlatformRows = data.platformRows || []
      this.selectedStoreRows = data.storeRows || []
      this.selectedProductRows = data.productRows || []
    },
    handlePreviewMaterial (payload) {
      this.$emit('preview-material', payload)
    },
    handleExceptionConfirmChange (val) {
      this.exceptionConfirmed = !!val
    },
    handlePrecheckChange (data) {
      this.precheckData = data || {}
    },
    handlePlanRowsChange (rows) {
      this.planRows = Array.isArray(rows) ? rows : []
    },
    handlePrev () {
      if (this.activeStep > 1) {
        this.activeStep -= 1
      }
    },
    async handleNext () {
      if (this.activeStep >= 3 || this.nextLoading) return
      if (this.activeStep === 1) {
        if (!this.selectedProductRows.length) {
          this.$message.warning('请先选择商品')
          return
        }
        this.exceptionConfirmed = false
        this.activeStep = 2
        await this.$nextTick()
        const stepComponent = this.$refs.stepComponent
        if (stepComponent && stepComponent.handlePrecheck) {
          this.nextLoading = true
          try {
            await stepComponent.handlePrecheck()
          } finally {
            this.nextLoading = false
          }
        }
        return
      }
      if (this.activeStep === 2 && !this.exceptionConfirmed) {
        this.$message.warning('请先确认已完成人工检查')
        return
      }
      this.activeStep += 1
    },
    async handleFinish () {
      if (this.finishLoading) return
      const stepComponent = this.$refs.stepComponent
      if (!stepComponent || !stepComponent.handleCreateTask) return
      this.finishLoading = true
      try {
        const taskResult = await stepComponent.handleCreateTask()
        if (!taskResult) return
        this.$emit('finish', {
          rowData: this.rowData,
          selectedPlatformRows: this.selectedPlatformRows,
          selectedStoreRows: this.selectedStoreRows,
          selectedProductRows: this.selectedProductRows,
          taskResult
        })
        this.drawerVisible = false
      } finally {
        this.finishLoading = false
      }
    },
    lineBreak (value) {
      return value ? String(value).replace(/,/g, '\n') : '-'
    }
  }
}
</script>

<style lang="stylus" scoped>
.drawer-shell
  height 100%
  display flex
  flex-direction column
  background #fff

.drawer-head,
.drawer-foot
  flex 0 0 auto
  display flex
  align-items center
  justify-content space-between
  padding 14px 20px
  border-bottom 1px solid #ebeef5
  background #fff
  position sticky
  z-index 2

.drawer-head
  top 0
  font-size 16px
  font-weight 600

.drawer-foot
  bottom 0
  border-bottom 0
  border-top 1px solid #ebeef5
  justify-content flex-end
  gap 12px

.drawer-body
  flex 1 1 auto
  overflow auto
  padding 16px 20px 24px
  background #f7f8fa

.section-block
  margin-bottom 16px

.section-title
  font-size 14px
  font-weight 600
  color #303133
  margin-bottom 10px

.summary-grid
  display grid
  grid-template-columns repeat(3, minmax(0, 1fr))
  gap 12px

.summary-card
  min-height 72px
  background #fff
  border 1px solid #ebeef5
  border-radius 6px
  padding 12px 14px

.summary-label
  color #909399
  font-size 12px
  margin-bottom 8px

.summary-value
  color #303133
  font-size 14px
  font-weight 600
  word-break break-all

.process-stepper
  display flex
  gap 12px
  margin-bottom 8px

.process-step
  flex 1
  min-height 56px
  border 1px solid #dcdfe6
  border-radius 6px
  background #fff
  display flex
  align-items center
  gap 10px
  padding 12px

.process-step span
  width 24px
  height 24px
  line-height 24px
  text-align center
  border-radius 50%
  background #c0c4cc
  color #fff
  flex-shrink 0

.process-step.active
  border-color #409eff

.process-step.active span,
.process-step.done span
  background #409eff

.step-desc
  color #606266
  font-size 12px

.multiline-cell-text
  white-space pre-line
  word-break break-all

.drawer-close
  padding 0

@media (max-width: 1200px)
  .summary-grid
    grid-template-columns repeat(2, minmax(0, 1fr))
</style>
